import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, ListGroup, Form, Button, Badge, Modal, Alert } from 'react-bootstrap';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Select from 'react-select';
import { API_HOST_BASE } from '../utils/apiUtils';
import './Conversations.css';

const Conversations = () => {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  
  // New conversation flow states
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [newConversationMessage, setNewConversationMessage] = useState('');
  const [startingConversation, setStartingConversation] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('connecting');
  const [pendingMessages, setPendingMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const userRef = useRef(user);
  const selectedConversationRef = useRef(selectedConversation);
  const initialLoadDoneRef = useRef(false);
  const connectionErrorCountRef = useRef(0);
  const messagesCacheRef = useRef(new Map());
  const loadingConversationRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!token) {
      setConnectionState('disconnected');
      return;
    }

    setConnectionState('connecting');
    let reconnectTimeout;
    let connectionAttemptTimeout;
    
    const newSocket = io(API_HOST_BASE, {
      auth: { token },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // 60s so first connection survives Render Free tier cold start (~50s)
      timeout: 60000
    });

    connectionAttemptTimeout = setTimeout(() => {
      if (!newSocket.connected) {
        console.log('Socket connection taking longer than expected...');
      }
    }, 5000);

    newSocket.on('connect', () => {
      console.log('✅ Connected to conversation server');
      setIsSocketConnected(true);
      setConnectionState('connected');
      connectionErrorCountRef.current = 0;
      setError('');
      
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (connectionAttemptTimeout) clearTimeout(connectionAttemptTimeout);

      // Re-join current conversation room after connect/reconnect so we receive events
      const currentConv = selectedConversationRef.current;
      if (currentConv?.conversation_id) {
        newSocket.emit('join_conversation', currentConv.conversation_id);
      }

      // Send any pending messages
      setPendingMessages(prev => {
        if (prev.length > 0) {
          prev.forEach(msg => {
            newSocket.emit('send_message', msg);
          });
          return [];
        }
        return prev;
      });
    });

    newSocket.on('disconnect', (reason) => {
      console.log('❌ Disconnected:', reason);
      setIsSocketConnected(false);
      setConnectionState('reconnecting');
      
      if (reason === 'io server disconnect') {
        reconnectTimeout = setTimeout(() => {
          newSocket.connect();
        }, 500);
      }
    });

    newSocket.on('connect_error', (err) => {
      console.error('❌ Connection error:', err.message);
      setIsSocketConnected(false);
      connectionErrorCountRef.current += 1;
      
      if (connectionErrorCountRef.current >= 3) {
        setConnectionState('disconnected');
      } else {
        setConnectionState('reconnecting');
      }
    });

    newSocket.on('reconnect_attempt', () => {
      setConnectionState('reconnecting');
    });

    newSocket.on('reconnect', () => {
      setIsSocketConnected(true);
      setConnectionState('connected');
      connectionErrorCountRef.current = 0;
      setError('');
      // Re-join current conversation room after reconnect
      const currentConv = selectedConversationRef.current;
      if (currentConv?.conversation_id) {
        newSocket.emit('join_conversation', currentConv.conversation_id);
      }
    });

    newSocket.on('reconnect_failed', () => {
      setIsSocketConnected(false);
      setConnectionState('disconnected');
      setError('Connection lost. Please refresh the page.');
    });

    newSocket.on('new_message', (message) => {
      const normalized = { ...message, isOwn: message.isOwn ?? (message.sender_id === userRef.current?.user_id) };
      setMessages(prev => [...prev, normalized]);
      
      const convId = message.conversation_id;
      if (convId && messagesCacheRef.current.has(convId)) {
        const cached = messagesCacheRef.current.get(convId);
        messagesCacheRef.current.set(convId, [...cached, normalized]);
      }
    });

    newSocket.on('message_sent', (message) => {
      const normalized = { ...message, isOwn: true };
      setMessages(prev => [...prev, normalized]);
      
      const convId = message.conversation_id;
      if (convId && messagesCacheRef.current.has(convId)) {
        const cached = messagesCacheRef.current.get(convId);
        messagesCacheRef.current.set(convId, [...cached, normalized]);
      }
    });

    newSocket.on('user_typing', (data) => {
      setTypingUsers(prev => {
        const filtered = prev.filter(u => u.userId !== data.userId);
        return [...filtered, { userId: data.userId, userName: data.userName }];
      });
    });

    newSocket.on('user_stop_typing', (data) => {
      setTypingUsers(prev => prev.filter(u => u.userId !== data.userId));
    });

    newSocket.on('access_denied', (data) => {
      console.error('Access denied:', data);
      setError(`Access denied: ${data.error}`);
      setConversations(prev => prev.filter(c => c.conversation_id !== data.conversationId));
      setSelectedConversation(prev => 
        prev?.conversation_id === data.conversationId ? null : prev
      );
    });

    newSocket.on('message_error', (data) => {
      console.error('Message error:', data);
      setError(data.error || 'Failed to send message. Please try again.');
    });

    setSocket(newSocket);

    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (connectionAttemptTimeout) clearTimeout(connectionAttemptTimeout);
      newSocket.removeAllListeners();
      newSocket.disconnect();
      setIsSocketConnected(false);
      setConnectionState('disconnected');
    };
  }, [token]);

  const loadConversations = useCallback(async (selectFirst = false) => {
    try {
      const response = await axios.get('/conversations');
      setConversations(response.data);
      
      if (selectFirst && response.data.length > 0 && !selectedConversationRef.current) {
        setSelectedConversation(response.data[0]);
      }
      
      setLoading(false);
      setError('');
      initialLoadDoneRef.current = true;
    } catch (error) {
      console.error('Error loading conversations:', error);
      if (initialLoadDoneRef.current || error?.response?.status) {
        setError('Failed to load conversations');
      }
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId, forceRefresh = false) => {
    loadingConversationRef.current = conversationId;
    
    const cachedMessages = messagesCacheRef.current.get(conversationId);
    if (cachedMessages && !forceRefresh) {
      setMessages(cachedMessages);
      setMessagesLoading(false);
      
      axios.get(`/conversations/${conversationId}/messages`)
        .then(response => {
          if (loadingConversationRef.current === conversationId) {
            const normalized = response.data.map(m => ({
              ...m,
              isOwn: m.sender_id === userRef.current?.user_id
            }));
            setMessages(normalized);
            messagesCacheRef.current.set(conversationId, normalized);
          }
        })
        .catch(err => console.error('Background message refresh failed:', err));
      return;
    }
    
    setMessagesLoading(true);
    setMessages([]);
    
    try {
      const response = await axios.get(`/conversations/${conversationId}/messages`);
      
      if (loadingConversationRef.current !== conversationId) {
        return;
      }
      
      const normalized = response.data.map(m => ({
        ...m,
        isOwn: m.sender_id === userRef.current?.user_id
      }));
      setMessages(normalized);
      setMessagesLoading(false);
      messagesCacheRef.current.set(conversationId, normalized);
      
      setConversations(prev => prev.map(c => 
        c.conversation_id === conversationId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Error loading messages:', error);
      if (loadingConversationRef.current === conversationId) {
        setMessagesLoading(false);
        setError('Failed to load messages');
      }
    }
  }, []);

  useEffect(() => {
    loadConversations(true);
  }, [loadConversations]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.conversation_id);
      socket?.emit('join_conversation', selectedConversation.conversation_id);
      setConversations(prev => prev.map(c => 
        c.conversation_id === selectedConversation.conversation_id
          ? { ...c, unread_count: 0 }
          : c
      ));
    }
  }, [selectedConversation, socket, loadMessages]);

  const loadAvailableUsers = async (searchTerm = '') => {
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await axios.get('/conversations/users/available', { params });
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Error loading available users:', error);
      setError('Failed to load available users');
    }
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const messagePayload = {
      conversationId: selectedConversation.conversation_id,
      content: newMessage.trim()
    };
    
    setNewMessage('');
    setIsTyping(false);
    
    if (!socket?.connected) {
      setPendingMessages(prev => [...prev, messagePayload]);
      socket?.connect();
      return;
    }
    
    socket.emit('send_message', messagePayload);
    socket.emit('typing_stop', selectedConversation.conversation_id);
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    
    if (!isTyping && selectedConversation) {
      setIsTyping(true);
      socket?.emit('typing_start', selectedConversation.conversation_id);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      socket?.emit('typing_stop', selectedConversation.conversation_id);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Start new conversation with first message
  const startConversation = async () => {
    if (!selectedRecipient || !newConversationMessage.trim()) return;
    
    setStartingConversation(true);
    try {
      const response = await axios.post('/conversations/start', {
        recipientId: selectedRecipient.user_id,
        messageText: newConversationMessage.trim()
      });
      
      const { conversation_id, is_new, other_user, message } = response.data;
      
      if (is_new) {
        // Add new conversation to list
        const newConv = {
          conversation_id,
          other_user_id: other_user.user_id,
          other_user_name: other_user.fullname,
          other_user_email: other_user.email,
          other_user_role: other_user.role,
          other_user_photo: other_user.profile_photo,
          last_message_content: message.content,
          last_message_created_at: message.created_at,
          last_message_sender: message.sender_name,
          unread_count: 0,
          message_count: 1
        };
        setConversations(prev => [newConv, ...prev]);
        setSelectedConversation(newConv);
      } else {
        // Conversation exists, just select it and refresh
        const existingConv = conversations.find(c => c.conversation_id === conversation_id);
        if (existingConv) {
          setSelectedConversation(existingConv);
        }
        loadConversations();
      }
      
      // Join the conversation room
      socket?.emit('join_conversation', conversation_id);
      
      // Reset modal state
      setShowNewConversation(false);
      setSelectedRecipient(null);
      setNewConversationMessage('');
    } catch (error) {
      console.error('Error starting conversation:', error);
      setError(error.response?.data?.error || 'Failed to start conversation');
    } finally {
      setStartingConversation(false);
    }
  };

  const handleConversationSelect = (conversationId) => {
    setSelectedConversations(prev => {
      if (prev.includes(conversationId)) {
        return prev.filter(id => id !== conversationId);
      } else {
        return [...prev, conversationId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedConversations.length === conversations.length) {
      setSelectedConversations([]);
    } else {
      setSelectedConversations(conversations.map(conv => conv.conversation_id));
    }
  };

  const toggleDeleteMode = () => {
    setIsDeleteMode(!isDeleteMode);
    if (isDeleteMode) {
      setSelectedConversations([]);
    }
  };

  const deleteSelectedConversations = async () => {
    if (selectedConversations.length === 0) return;

    try {
      setLoading(true);
      await axios.delete('/conversations/bulk', {
        data: { conversationIds: selectedConversations }
      });

      setConversations(prev => 
        prev.filter(conv => !selectedConversations.includes(conv.conversation_id))
      );
      setSelectedConversations([]);
      setShowDeleteConfirm(false);
      setIsDeleteMode(false);
      
      if (selectedConversations.includes(selectedConversation?.conversation_id)) {
        setSelectedConversation(null);
      }
    } catch (error) {
      console.error('Error deleting conversations:', error);
      setError('Failed to delete conversations');
    } finally {
      setLoading(false);
    }
  };

  const markAllConversationsAsRead = async () => {
    try {
      const unreadConversations = conversations.filter(c => c.unread_count > 0);
      await Promise.all(
        unreadConversations.map(conv => 
          axios.post(`/conversations/${conv.conversation_id}/read`)
        )
      );
      setConversations(prev => 
        prev.map(conv => ({ ...conv, unread_count: 0 }))
      );
    } catch (error) {
      console.error('Error marking all conversations as read:', error);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getAvailableUsersForSelect = () => {
    return availableUsers.map(u => ({
      value: u.user_id,
      label: `${u.fullname} (${u.role})`,
      user: u
    }));
  };

  const ConnectionIndicator = () => {
    if (connectionState === 'connected') return null;
    
    const states = {
      connecting: { text: 'Connecting...', variant: 'info', icon: 'bi-wifi' },
      reconnecting: { text: 'Reconnecting...', variant: 'warning', icon: 'bi-arrow-repeat' },
      disconnected: { text: 'Disconnected', variant: 'danger', icon: 'bi-wifi-off' }
    };
    
    const state = states[connectionState] || states.connecting;
    
    return (
      <Alert variant={state.variant} className="mb-2 py-2 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <i className={`bi ${state.icon} me-2 ${connectionState === 'reconnecting' ? 'spin-animation' : ''}`}></i>
          <span>{state.text}</span>
        </div>
        {connectionState === 'disconnected' && (
          <Button 
            size="sm" 
            variant="outline-danger" 
            onClick={() => {
              setConnectionState('reconnecting');
              socket?.connect();
            }}
          >
            Retry
          </Button>
        )}
      </Alert>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      <ConnectionIndicator />
      <div className="row g-0">
        {/* Conversations Sidebar */}
        <div className="col-md-3">
          <Card style={{ border: '1px solid #000000', backgroundColor: '#ffffff', marginRight: '10px' }}>
            <Card.Header className="d-flex justify-content-between align-items-center" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
              <h5 className="mb-0">Messages</h5>
              <div className="d-flex gap-1" style={{ flexShrink: 0 }}>
                {isDeleteMode ? (
                  <>
                    {selectedConversations.length > 0 && (
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => setShowDeleteConfirm(true)}
                        title={`Delete ${selectedConversations.length} conversation${selectedConversations.length > 1 ? 's' : ''}`}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    )}
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={toggleDeleteMode}
                      title="Cancel"
                    >
                      <i className="bi bi-x"></i>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={toggleDeleteMode}
                      title="Delete conversations"
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                    {conversations.some(c => c.unread_count > 0) && (
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={markAllConversationsAsRead}
                        title="Mark all as read"
                      >
                        <i className="bi bi-check2-all"></i>
                      </Button>
                    )}
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => {
                        setShowNewConversation(true);
                        loadAvailableUsers();
                      }}
                      title="New message"
                    >
                      <i className="bi bi-plus"></i>
                    </Button>
                  </>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {conversations.length > 0 ? (
                <>
                  {isDeleteMode && (
                    <div 
                      className="p-2 border-bottom"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAll();
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Check
                        type="checkbox"
                        label="Select All"
                        checked={selectedConversations.length === conversations.length && conversations.length > 0}
                        onChange={() => {}} // Handled by parent div
                        readOnly
                      />
                    </div>
                  )}
                  <ListGroup variant="flush">
                    {conversations.map(conversation => (
                      <ListGroup.Item
                        key={conversation.conversation_id}
                        action
                        active={selectedConversation?.conversation_id === conversation.conversation_id}
                        onClick={(e) => {
                          if (e.target.type === 'checkbox' || e.target.closest('.form-check')) return;
                          setSelectedConversation(conversation);
                        }}
                        className="d-flex justify-content-between align-items-start"
                      >
                        <div className="d-flex align-items-start w-100">
                          {isDeleteMode && (
                            <div 
                              className="me-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleConversationSelect(conversation.conversation_id);
                              }}
                            >
                              <Form.Check
                                type="checkbox"
                                checked={selectedConversations.includes(conversation.conversation_id)}
                                onChange={() => {}} // Handled by parent div
                                readOnly
                              />
                            </div>
                          )}
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="fw-bold d-flex justify-content-between">
                              <span className="text-truncate">{conversation.other_user_name}</span>
                              {conversation.last_message_created_at && (
                                <small className="text-muted ms-2 flex-shrink-0">
                                  {formatTime(conversation.last_message_created_at)}
                                </small>
                              )}
                            </div>
                            <small className="text-muted text-truncate d-block">
                              {conversation.last_message_content || 'No messages yet'}
                            </small>
                          </div>
                        </div>
                        {conversation.unread_count > 0 && (
                          <Badge bg="danger" className="ms-2 flex-shrink-0">
                            {conversation.unread_count}
                          </Badge>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-chat-dots" style={{ fontSize: '2rem' }}></i>
                  <p className="mt-2 mb-0">No conversations yet</p>
                  <Button 
                    variant="link" 
                    size="sm"
                    onClick={() => {
                      setShowNewConversation(true);
                      loadAvailableUsers();
                    }}
                  >
                    Start a conversation
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Messages Panel */}
        <div className="col-md-9">
          <Card style={{ border: '1px solid #000000', backgroundColor: '#ffffff', marginLeft: '10px' }}>
            <Card.Header>
              {selectedConversation ? (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">{selectedConversation.other_user_name}</h5>
                    <small className="text-muted">{selectedConversation.other_user_role}</small>
                  </div>
                </div>
              ) : (
                <h5 className="mb-0">Select a conversation</h5>
              )}
            </Card.Header>
            
            {selectedConversation ? (
              <>
                <Card.Body className="conversation-messages" style={{ height: '400px', overflowY: 'auto' }}>
                  {messagesLoading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading messages...</span>
                      </div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                      <div className="text-center">
                        <i className="bi bi-chat-dots" style={{ fontSize: '2rem' }}></i>
                        <p className="mt-2 mb-0">No messages yet. Start the conversation!</p>
                      </div>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div key={message.message_id} className={`message ${message.isOwn ? 'own-message' : ''}`}>
                        <div className="message-header">
                          <span className="message-sender">{message.sender_name}</span>
                          <span className="message-time">{formatTime(message.created_at)}</span>
                        </div>
                        <div className="message-content">
                          {message.content}
                        </div>
                      </div>
                    ))
                  )}
                  
                  {typingUsers.length > 0 && (
                    <div className="typing-indicator">
                      <em>{typingUsers.map(u => u.userName).join(', ')} is typing...</em>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </Card.Body>

                <Card.Footer>
                  <Form onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
                    <div className="d-flex">
                      <Form.Control
                        type="text"
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={handleTyping}
                        onKeyPress={handleKeyPress}
                        className="me-2"
                      />
                      <Button type="submit" variant="primary" disabled={!newMessage.trim()}>
                        Send
                      </Button>
                    </div>
                  </Form>
                </Card.Footer>
              </>
            ) : (
              <Card.Body className="text-center text-muted" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div>
                  <i className="bi bi-chat-dots" style={{ fontSize: '3rem' }}></i>
                  <p className="mt-3">Select a conversation or start a new one</p>
                </div>
              </Card.Body>
            )}
          </Card>
        </div>
      </div>

      {/* New Conversation Modal */}
      <Modal show={showNewConversation} onHide={() => {
        setShowNewConversation(false);
        setSelectedRecipient(null);
        setNewConversationMessage('');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>New Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>To:</Form.Label>
              <Select
                value={selectedRecipient ? {
                  value: selectedRecipient.user_id,
                  label: `${selectedRecipient.fullname} (${selectedRecipient.role})`,
                  user: selectedRecipient
                } : null}
                onChange={(option) => setSelectedRecipient(option?.user || null)}
                options={getAvailableUsersForSelect()}
                placeholder="Search for a user..."
                isSearchable={true}
                isClearable={true}
                onInputChange={(inputValue) => {
                  if (inputValue) {
                    loadAvailableUsers(inputValue);
                  }
                }}
                noOptionsMessage={() => "No users found"}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: '1px solid #ced4da',
                    fontSize: '1em'
                  })
                }}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Message:</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Type your message..."
                value={newConversationMessage}
                onChange={(e) => setNewConversationMessage(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowNewConversation(false);
            setSelectedRecipient(null);
            setNewConversationMessage('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={startConversation}
            disabled={!selectedRecipient || !newConversationMessage.trim() || startingConversation}
          >
            {startingConversation ? 'Sending...' : 'Send Message'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Conversations</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {selectedConversations.length} conversation{selectedConversations.length > 1 ? 's' : ''}?</p>
          <p className="text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteSelectedConversations}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Alert */}
      {error && (
        <Alert 
          variant="danger" 
          dismissible 
          onClose={() => setError('')}
          className="position-fixed bottom-0 end-0 m-3"
          style={{ zIndex: 1050, maxWidth: '400px' }}
        >
          {error}
        </Alert>
      )}
    </div>
  );
};

export default Conversations;
